/**
 * StockShopService
 * Service for public shop stock endpoints in VendorCatalogManagement (read-only + interest)
 * Route: api/shop
 * Controller: ShopController.cs (CCMS.Api.VendorCatalogManagement)
 */
import { BaseService } from '../base/BaseService';
/**
 * StockShopService
 * Public endpoints for stock status, cart validation, and interest tracking.
 */
export class StockShopService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? StockShopService.DEFAULT_SERVICE_PATH,
            basePath: StockShopService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== STOCK STATUS ====================
    /**
     * Get stock status for all products at an event
     * GET /api/shop/events/{eventId}/stock
     */
    async getEventStock(eventId) {
        const response = await this.getEventStockRaw(eventId);
        return response.data ?? [];
    }
    /**
     * Get event stock (raw response)
     */
    getEventStockRaw(eventId) {
        const endpoint = this.getEndpoint('event-stock', `events/${eventId}/stock`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get stock status for a single product at an event
     * GET /api/shop/events/{eventId}/products/{productId}/stock
     */
    async getProductStock(eventId, productId) {
        const response = await this.getProductStockRaw(eventId, productId);
        if (!response.data) {
            throw new Error(`Stock not found for product ${productId} at event ${eventId}`);
        }
        return response.data;
    }
    /**
     * Get product stock (raw response)
     */
    getProductStockRaw(eventId, productId) {
        const endpoint = this.getEndpoint('product-stock', `events/${eventId}/products/${productId}/stock`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== CART VALIDATION ====================
    /**
     * Validate cart items against current stock levels
     * POST /api/shop/events/{eventId}/validate-cart
     */
    async validateCart(eventId, request) {
        const response = await this.validateCartRaw(eventId, request);
        if (!response.data) {
            throw new Error('Cart validation failed');
        }
        return response.data;
    }
    /**
     * Validate cart (raw response)
     */
    validateCartRaw(eventId, request) {
        const endpoint = this.getEndpoint('validate-cart', `events/${eventId}/validate-cart`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    // ==================== INTEREST TRACKING ====================
    /**
     * Record interest (item added to cart) - increments Redis counter with 15min TTL
     * POST /api/shop/events/{eventId}/interest
     */
    async recordInterest(eventId, request) {
        const endpoint = this.getEndpoint('record-interest', `events/${eventId}/interest`);
        const url = this.buildUrl(endpoint);
        await this.http.post(url, request);
    }
    /**
     * Remove interest (item removed from cart) - decrements Redis counter
     * POST /api/shop/events/{eventId}/interest/remove
     */
    async removeInterest(eventId, request) {
        const endpoint = this.getEndpoint('remove-interest', `events/${eventId}/interest/remove`);
        const url = this.buildUrl(endpoint);
        await this.http.post(url, request);
    }
}
StockShopService.DEFAULT_SERVICE_PATH = 'vendor-catalogs';
StockShopService.BASE_PATH = 'api/shop';
//# sourceMappingURL=StockShopService.js.map